import { applyCoreAttackStyle } from './coreAttackStyles';
import { getTotalShardsAcquired } from './shardTotals';
import {
  BOARD_TILE_ABILITIES,
  getBoardSquareLabel,
  spendGrowthTile,
} from './doorBoard';
import {
  getChainReactionIndices,
  getTriggeredBoardSquares,
  hasChainReaction,
} from './chainReaction';
import { RELICS } from './relics';
import { formatDieValue, rollDie } from './dice';
import {
  boostPowerUpRoll,
  getPowerUpMultiplier,
  getPowerUpMultipliers,
  upgradePowerUpTile,
} from './powerUp';
import { CORE_SECTOR, getBoardEncounterRound } from './runConstants';

export const RECHARGE_ABILITIES = ['phaseAnchor'];
export const getRechargeOptions = (game) =>
  RECHARGE_ABILITIES.filter(
    (id) => game.relics.includes(id) && game[id]?.usedFloor === game.floor
  );

// A saved Recharge choice may still contain the retired Chain Reaction option.
export const migrateRechargeChoice = (game) => {
  const resolution = game.boardTileResolution;
  const pending = resolution?.pending;
  if (
    pending?.type !== 'recharge' ||
    !pending.options?.includes('chainReaction')
  )
    return game;
  const options = getRechargeOptions(game);
  if (options.length)
    return {
      ...game,
      boardTileResolution: { ...resolution, pending: { ...pending, options } },
    };
  return advanceBoardTileEffects({
    ...game,
    boardTileResolution: {
      ...resolution,
      pending: null,
      entries: resolution.entries.map((entry, index) =>
        index === resolution.cursor ? { ...entry, done: true } : entry
      ),
      cursor: resolution.cursor + 1,
      logs: [...resolution.logs, 'Recharge: no spent Phase Anchor to restore.'],
    },
  });
};

const copySquare = (square) => ({
  ...square,
  ...(square.effect ? { effect: { ...square.effect } } : {}),
});

export const sabotageCoreBoard = (board) => {
  const changes = [];
  const transformedBoard = (Array.isArray(board) ? board : []).map(
    (square, index) => {
      const before = copySquare(square);
      const after = copySquare(square);
      const categories = [];
      if (after.effect?.type === 'critical') {
        after.effect.type = 'criticalMiss';
        categories.push('critical');
      }
      if (Number.isFinite(after.modifier) && after.modifier > 0) {
        after.modifier = -after.modifier;
        categories.push('modifier');
      }
      if (categories.length) {
        changes.push({ index, before, after: copySquare(after), categories });
        return after;
      }
      return before;
    }
  );
  return { board: transformedBoard, changes };
};
export const getPendingBoardTile = (game) => {
  const state = game.boardTileResolution;
  return state?.floor === game.floor &&
    state.landingIndex === game.landingIndex &&
    state.round === getBoardEncounterRound(game)
    ? state.pending || null
    : null;
};
export const needsBoardTileChoice = (game) => {
  const pending = getPendingBoardTile(game);
  return !!pending && !(pending.type === 'gambit' && pending.relaunch);
};

const relayOptions = (entry, entries) =>
  getChainReactionIndices(entry.index)
    .slice(1)
    .filter(
      (index) =>
        !entry.path.includes(index) &&
        !entries.some((other) => other !== entry && other.path.includes(index))
    );

// Each entry belongs to one actual activation. Relay follows a path without
// revisiting cells, so routed effects cannot create a loop.
export const prepareBoardTileEffects = (game, options = {}) => {
  const saved = game.boardTileResolution;
  // Resume old saves that paused a chain at a neighboring Power Up. Rebuild
  // that request under the contact-only relaunch rule before presenting it.
  if (
    saved?.pending?.type === 'powerUp' &&
    !saved.pending.afterChain &&
    hasChainReaction(game)
  )
    return advanceBoardTileEffects({
      ...game,
      chainReaction: { used: true, usedFloor: game.floor },
      boardTileResolution: { ...saved, pending: null },
    });
  if (saved) {
    // Older saves can already have committed this reward without replacing
    // its board tile. Keep the saved reward; spend only completed entries.
    const activated = new Set(
      (saved.entries || [])
        .filter(
          ({ done, square }) =>
            done && square.effect?.type === 'heal' && square.effect.amount === 2
        )
        .map(({ index }) => index)
    );
    let changed = false;
    const doorBoard = game.doorBoard.map((square, index) => {
      const next = activated.has(index) ? spendGrowthTile(square) : square;
      if (next !== square) changed = true;
      return next;
    });
    return changed ? { ...game, doorBoard } : game;
  }
  if (!Number.isInteger(game.landingIndex)) return game;
  const carried = options.carriedEntries || [];
  return advanceBoardTileEffects({
    ...game,
    boardTileResolution: {
      floor: game.floor,
      round: getBoardEncounterRound(game),
      landingIndex: game.landingIndex,
      entries: [
        ...carried,
        ...getTriggeredBoardSquares(game).map(({ index, square }) => ({
          index,
          sourceIndex: index,
          square: copySquare(square),
          path: [index],
          done: false,
        })),
      ],
      cursor: carried.length,
      pending: null,
      logs: [...(options.logs ?? game.powerUp?.logs ?? [])],
      interference: options.interference ?? game.powerUp?.interference ?? 0,
      gambitUsed: options.gambitUsed ?? game.powerUp?.gambitUsed ?? false,
      finalized: false,
    },
  });
};

export const advanceBoardTileEffects = (game) => {
  const next = { ...game };
  const resolution = game.boardTileResolution;
  if (!resolution || resolution.pending || resolution.finalized) return game;
  const state = {
    ...resolution,
    entries: resolution.entries.map((entry) => ({ ...entry })),
    logs: [...resolution.logs],
  };
  next.boardTileResolution = state;
  while (state.cursor < state.entries.length) {
    const entry = state.entries[state.cursor];
    const type = entry.square.effect?.type;
    const ability = BOARD_TILE_ABILITIES[type];
    if (!ability) {
      // The entry keeps the original reward for this throw, while the board
      // immediately exposes the spent hazard to subsequent landings.
      if (type === 'heal' && entry.square.effect.amount === 2)
        next.doorBoard = next.doorBoard.map((square, index) =>
          index === entry.index ? spendGrowthTile(square) : square
        );
      entry.done = true;
      state.cursor += 1;
      continue;
    }
    if (ability.coreOnly && next.floor !== CORE_SECTOR) {
      state.logs.push(`${ability.name} is inactive outside the Core arena.`);
    } else if (type === 'powerUp') {
      const multiplier = getPowerUpMultiplier(entry.square.effect);
      const request = {
        id: `${state.floor}:${state.round ?? 'room'}:${
          state.cursor
        }:${entry.path.join('-')}:powerUp:${next.powerUp?.boosts || 0}`,
        type,
        multiplier,
        index: entry.index,
        before: next.roll,
        after: boostPowerUpRoll(next.roll, multiplier),
        options: [],
      };
      if (hasChainReaction(next)) {
        if (entry.index === state.landingIndex && entry.path.length === 1) {
          // Finish every tile in this chain before launching from the tile
          // the die physically landed on.
          state.deferredPowerUp = { ...request, afterChain: true };
        } else {
          // A neighboring Power Up is a board bonus, applied in chain order.
          // It upgrades once without starting another physical landing.
          entry.powerUpMultiplier = multiplier;
          next.doorBoard = next.doorBoard.map((square, index) =>
            index === entry.index ? upgradePowerUpTile(square) : square
          );
          state.logs.push(
            `Chain Reaction: POWER UP square ${entry.index + 1} adds +${
              20 * multiplier
            }% without relaunching the die.`
          );
        }
      } else {
        state.pending = request;
        return next;
      }
    } else if (type === 'trap') {
      state.logs.push(
        'Trap: turn skipped; your attack is canceled. A surviving enemy attacks twice.'
      );
    } else if (type === 'interference') {
      state.interference += 1;
      state.logs.push(
        'Interference: The Core’s attacks are 1 damage weaker this round.'
      );
    } else if (type === 'sabotage') {
      const { board, changes } = sabotageCoreBoard(next.bossDoorBoard);
      if (changes.length) {
        // rollBoss reuses this saved board through the Core's next throw;
        // nextBossRound generates a fresh board after that round resolves.
        next.bossDoorBoard = board;
        const criticalCount = changes.filter((change) =>
          change.categories.includes('critical')
        ).length;
        const modifierCount = changes.filter((change) =>
          change.categories.includes('modifier')
        ).length;
        state.pending = {
          id: `${state.floor}:${state.round ?? 'room'}:${
            state.cursor
          }:${entry.path.join('-')}:sabotage`,
          type,
          cutscene: true,
          changes,
          criticalCount,
          modifierCount,
          options: [],
          index: entry.index,
        };
        state.logs.push(
          `Sabotage turned ${criticalCount} Critical Hit tiles into Critical Misses and reversed ${modifierCount} positive modifiers on the Core’s next board.`
        );
        return next;
      }
      state.logs.push('Sabotage: the Core board had no advantages to corrupt.');
    } else {
      const options =
        type === 'relay'
          ? relayOptions(entry, state.entries)
          : type === 'recharge'
          ? getRechargeOptions(next)
          : type === 'gambit' && !state.gambitUsed
          ? ['keep', 'reroll']
          : [];
      if (options.length) {
        state.pending = {
          id: `${state.floor}:${state.round ?? 'room'}:${
            state.cursor
          }:${entry.path.join('-')}:${type}`,
          type,
          options,
          index: entry.index,
        };
        return next;
      }
      state.logs.push(
        type === 'relay'
          ? 'Relay: every neighboring tile is already in this activation.'
          : type === 'recharge'
          ? 'Recharge: no spent floor ability to restore.'
          : type === 'gambit'
          ? 'Gambit: this throw’s reroll has already been offered.'
          : `${ability.name}: no target available.`
      );
    }
    entry.done = true;
    state.cursor += 1;
  }
  if (state.deferredPowerUp) {
    state.pending = state.deferredPowerUp;
    state.deferredPowerUp = null;
  }
  return next;
};

// The choice ID is saved with its options and checked on every click. A stale
// click cannot spend another shard or accidentally choose the next queued tile.
export const chooseBoardTileEffect = (game, choice, random = Math.random) => {
  const pending = getPendingBoardTile(game);
  if (
    !pending ||
    choice?.id !== pending.id ||
    game.bossRoundState?.attack ||
    !['rolled', 'bossPlayerRolled', 'bossRolled'].includes(game.phase)
  )
    return game;
  const state = {
    ...game.boardTileResolution,
    pending: null,
    entries: game.boardTileResolution.entries.map((entry) => ({ ...entry })),
    logs: [...game.boardTileResolution.logs],
  };
  const entry = state.entries[state.cursor];
  const next = { ...game, boardTileResolution: state };
  const { value } = choice;
  switch (pending.type) {
    case 'relay':
      if (
        !pending.options.includes(value) ||
        !relayOptions(entry, state.entries).includes(value)
      )
        return game;
      entry.index = value;
      entry.square = copySquare(game.doorBoard[value]);
      entry.path = [...entry.path, value];
      state.logs.push(
        `Relay routed to square ${value + 1}: ${getBoardSquareLabel(
          entry.square
        )}.`
      );
      return advanceBoardTileEffects(next);
    case 'gambit':
      if (
        !pending.options.includes(value) ||
        (value === 'reroll' && game.shards < 1)
      )
        return game;
      state.gambitUsed = true;
      if (value === 'reroll') {
        const naturalRoll = applyCoreAttackStyle(
          game,
          rollDie(game.selectedDie, random)
        );
        let after = naturalRoll;
        if (next.powerUp)
          for (const multiplier of getPowerUpMultipliers(next.powerUp))
            after = boostPowerUpRoll(after, multiplier);
        next.shards = game.shards - 1;
        next.totalShardsAcquired = getTotalShardsAcquired(game);
        next.flipped = false;
        next.roll = after;
        if (next.powerUp)
          next.powerUp = { ...next.powerUp, naturalRoll, gambitUsed: true };
        state.pending = {
          id: `${pending.id}:relaunch`,
          type: 'gambit',
          relaunch: true,
          index: game.landingIndex,
          before: game.roll,
          naturalRoll,
          after,
          options: [],
        };
        state.logs.push('Gambit: spent 1 shard; the die is rerolling.');
        return next;
      }
      state.logs.push(
        `Gambit: kept current roll ${formatDieValue(game.roll)}.`
      );
      break;
    case 'sabotage':
      if (!pending.cutscene || value !== 'complete') return game;
      break;
    case 'recharge':
      if (
        !pending.options.includes(value) ||
        !getRechargeOptions(game).includes(value)
      )
        return game;
      next[value] = { ...game[value], usedFloor: null, armed: false };
      state.logs.push(
        `Recharge restored ${RELICS[value].name} for your next throw.`
      );
      break;
    default:
      return game;
  }
  entry.done = true;
  state.cursor += 1;
  return advanceBoardTileEffects(next);
};
